import React, { useEffect, useState } from "react";
import Dropdown from "../../components/common/Dropdown";
import Loader from "../../components/common/Loader";
// api/endpoints imports removed — using `fetchAreaOptions` from selectUBTSlice instead
import {
  EditEmployee,
  fetchRoleOptions,
  editAccess,
  addAccess,
} from "../../store/slices/manageAccessSlice";
import {
  fetchAreaOptions,
  fetchCenterOptions,
  fetchLocationOptions,
  resetDependentDropdowns,
} from "../../store/slices/selectUBTSlice";
import {
  getAllowedAreaCodes,
  getAllowedCenterCodes,
  getAllowedLocationCodes,
} from "../../utils/editPrivilegeCheck";
import type { Privilege } from "../../store/slices/profileSlice";
import { useDispatch, useSelector } from "react-redux";
import type { AppDispatch, RootState } from "../../store/store";

interface EditAccessFormProps {
  employee: EditEmployee;
  onClose: () => void;
  onSave: (updated: EditEmployee) => void;
  onError?: (message?: string, header?: string, variant?: string) => void;
  mode?: "add" | "edit";
}
const formatLocation = (loc?: string): string => {
  if (!loc) return "";
  // Use split/join to avoid super-linear backtracking from /\s*-\s*/g
  return loc.split("-").map((part) => part.trim()).filter(Boolean).join(" ");
};
const areaOptionsStatic = ["All"];
const centerOptionsStatic = ["All"];
const locationOptionsStatic = ["All"];
// roleOptions will be loaded from API via manageAccess slice; fall back to these defaults
const defaultRoleOptions = ["Administrator", "View-Only"];
// Used as fallback for restricted admins so that the "All" sentinel option is
// never injected into dropdowns when the user does not have wildcard access.
const NO_FALLBACK_OPTIONS: string[] = [];

const ALL_OPTION = { id: "All", label: "All", value: "All" };

/**
 * Filters an array of dropdown options to only those the logged-in user is
 * permitted to assign, based on their editDataPrivileges.
 *
 * - When `allowedCodes` is `null` the privilege level has no restriction
 *   (e.g. `areas: null`), so every option — including "All" — is kept intact.
 * - When `allowedCodes` is a non-null Set the user is restricted to specific
 *   codes: only matching options are shown and the "All" sentinel is excluded
 *   because the user cannot grant broader access than their own scope.
 *
 * @param options - Dropdown options returned from the selectUBT API.
 * @param allowedCodes - Set of permitted codes, or null for unrestricted access.
 * @returns Filtered dropdown options.
 */
function filterByPrivilege(
  options: { id: string | number; label: string; value: string }[],
  allowedCodes: Set<string> | null,
): { id: string | number; label: string; value: string }[] {
  // null means full access at this level — return all options unchanged
  if (allowedCodes === null) return options;
  // Restricted user: show only explicitly permitted codes; exclude "All" so
  // they cannot grant broader access than their own privilege scope.
  return options.filter((opt) => allowedCodes.has(String(opt.id)));
}

function isNullableScope(v?: string | number | null): boolean {
  if (v == null) return true;
  const s = String(v).trim();
  return s === "" || s === "All" || s === "Select";
}

function findOptionMatch(options: any[], target: string, suffix?: string): any {
  return options.find((o: any) => {
    if (!o) return false;
    const val = String(o.value ?? "").trim();
    const id = String(o.id ?? "").trim();
    const label = String(o.label ?? "").trim();
    const labelName = label.includes(" ")
      ? label.replace(/^\S+\s+/, "").trim()
      : label;
    if (
      val === target ||
      id === target ||
      label === target ||
      labelName === target
    )
      return true;
    if (suffix) {
          const re = new RegExp(String.raw`\s+${suffix}$`, "i");
      if (labelName.replace(re, "") === target.replace(re, "")) return true;
    }
    return labelName.toLowerCase().includes(target.toLowerCase());
  });
}

function normalizeRole(value?: string | null): string {
  return (value ?? "").split(/[-_\s]+/).join("").toLowerCase();
}

function isViewOnlyRole(r?: string): boolean {
  return normalizeRole(r) === "viewonly";
}

function isLmpRole(r?: string): boolean {
  return normalizeRole(r) === "lmp";
}

function isAdministratorRole(r?: string): boolean {
  return normalizeRole(r) === "administrator";
}

function resolveDropdownOptions(
  isViewOnly: boolean,
  selectOptions: any[],
  fallbackOptions: string[],
): { id: string | number; label: string; value: string }[] {
  if (isViewOnly) return [ALL_OPTION];
  if (selectOptions.length > 0) return selectOptions;
  return fallbackOptions.map((opt) => ({ id: opt, label: opt, value: opt }));
}

/**
 * Resolves the selected option for a dropdown, handling loading states.
 * @param isViewOnly - Whether the role is view-only (always returns ALL_OPTION)
 * @param selectOptions - Available options from Redux store
 * @param value - Current selected value
 * @param isLoading - Whether dropdowns are still loading (prevents "Select an option" flicker)
 * @returns The selected option object
 */
function resolveSelectedOption(
  isViewOnly: boolean,
  selectOptions: any[],
  value: string,
  isLoading: boolean = false,
): { id: string | number; label: string; value: string } {
  if (isViewOnly) return ALL_OPTION;
  if (isLoading && selectOptions.length === 0) {
    // During initial load, show the actual value as placeholder to avoid "Select an option"
    return { id: value, label: value, value };
  }
  if (selectOptions.length > 0) {
    return (
      selectOptions.find(
        (o: any) =>
          o.value === value || o.label === value || String(o.id) === value,
      ) ?? { id: value, label: value, value }
    );
  }
  return { id: value, label: value, value };
}

function preloadDropdownsForEmployee(
  dispatch: AppDispatch,
  regionCode: string,
  areaCode: any,
  centerCode: any,
): void {
  if (!regionCode) return;
  dispatch(fetchAreaOptions({ regionCode }));
  if (isNullableScope(areaCode)) return;
  dispatch(fetchCenterOptions({ regionCode, areaCode: String(areaCode) }));
  if (isNullableScope(centerCode)) return;
  dispatch(
    fetchLocationOptions({
      regionCode,
      areaCode: String(areaCode),
      centerCode: String(centerCode),
    }),
  );
}

/**
 * Synchronises a local dropdown state value when API options load, using fuzzy
 * matching to handle label/id/value discrepancies.
 *
 * @param options - API-loaded dropdown options.
 * @param employeeDesc - Employee's current responsibility description string.
 * @param setter - State setter to call with the resolved value.
 * @param suffix - Label suffix for `findOptionMatch` (e.g. "Area", "Center").
 */
function syncDropdownValue(
  options: any[],
  employeeDesc: string | null | undefined,
  setter: (value: string) => void,
  suffix: string,
): void {
  if (options.length === 0 || !employeeDesc) return;
  const target = employeeDesc.toString().trim();
  const match = findOptionMatch(options, target, suffix);
  if (match) setter(match.value ?? match.label ?? String(match.id));
}

interface RoleChangeSetters {
  setAreaOptions: (v: string[]) => void;
  setCenterOptions: (v: string[]) => void;
  setLocationOptions: (v: string[]) => void;
  setArea: (v: string) => void;
  setCenter: (v: string) => void;
  setLocation: (v: string) => void;
  setAreaId: (v: string) => void;
  setCenterId: (v: string) => void;
  setLocationId: (v: string) => void;
}

/**
 * Applies dropdown state changes when the assigned role changes.
 * Extracted to a module-level function to keep the role-change effect's
 * cognitive complexity low.
 *
 * @param role - The newly selected role.
 * @param isCrossRegionAdd - Whether this is a cross-region add operation.
 * @param resolvedRegionCode - The employee's resolved region code.
 * @param apiRegionCode - The region code used for API calls.
 * @param employee - The employee being edited.
 * @param dispatch - Redux dispatch function.
 * @param setters - State setter functions for dropdown values.
 */
function applyRoleChange(
  role: string,
  isCrossRegionAdd: boolean,
  resolvedRegionCode: string,
  apiRegionCode: string,
  employee: EditEmployee,
  dispatch: AppDispatch,
  setters: RoleChangeSetters,
): void {
  const {
    setAreaOptions, setCenterOptions, setLocationOptions,
    setArea, setCenter, setLocation,
    setAreaId, setCenterId, setLocationId,
  } = setters;

  if (isLmpRole(role)) {
    setAreaOptions(["All"]); setCenterOptions(["All"]); setLocationOptions(["All"]);
    setArea("All"); setCenter("All"); setLocation("All");
    setAreaId(""); setCenterId(""); setLocationId("");
    return;
  }
  if (isCrossRegionAdd && isViewOnlyRole(role)) {
    setCenterOptions(["All"]); setLocationOptions(["All"]);
    setCenter("All"); setCenterId(""); setLocation("All"); setLocationId("");
    if (resolvedRegionCode) dispatch(fetchAreaOptions({ regionCode: apiRegionCode }));
    return;
  }
  if (isViewOnlyRole(role)) {
    setAreaOptions(["All"]); setCenterOptions(["All"]); setLocationOptions(["All"]);
    setArea("All"); setCenter("All"); setLocation("All");
    return;
  }
  if (!isAdministratorRole(role) || !resolvedRegionCode) {
    setAreaOptions(areaOptionsStatic);
    setCenterOptions(centerOptionsStatic);
    setLocationOptions(locationOptionsStatic);
    return;
  }
  preloadDropdownsForEmployee(dispatch, apiRegionCode, employee.responsibilityArea, employee.responsibilityCenter);
}

interface ScopeValues {
  area: string;
  center: string;
  location: string;
  areaId: string;
  centerId: string;
  locationId: string;
  regionCode: string;
}

function buildAddPayload(
  employee: EditEmployee,
  role: string,
  scope: ScopeValues,
  primaryRegion: string,
) {
  return {
    nuid: employee.nuid,
    privRole: role,
    region: primaryRegion || null,
    area: isNullableScope(scope.area) ? null : scope.areaId || null,
    center: isNullableScope(scope.center) ? null : scope.centerId || null,
    location: isNullableScope(scope.location) ? null : scope.locationId || null,
  };
}

function buildEditPayload(
  employee: EditEmployee,
  role: string,
  scope: ScopeValues,
) {
  return {
    nuid: employee.nuid,
    oldPrivRole: employee.privRole,
    oldRegion: employee.responsibilityRegion ?? null,
    oldArea: employee.responsibilityArea ?? null,
    oldCenter: employee.responsibilityCenter ?? null,
    oldLocation: employee.responsibilityLocation ?? null,
    newPrivRole: role,
    newRegion: employee.responsibilityRegion ?? null,
    newArea: isNullableScope(scope.area) ? null : scope.areaId || null,
    newCenter: isNullableScope(scope.center) ? null : scope.centerId || null,
    newLocation: isNullableScope(scope.location)
      ? null
      : scope.locationId || null,
  };
}

function extractErrorMessage(error: unknown): string | undefined {
  if (typeof error === "string") return error;
  if (error && typeof error === "object") {
    const err = error as {
      message?: unknown;
      data?: { message?: unknown };
      response?: { data?: { message?: unknown } };
    };
    if (typeof err.message === "string") return err.message;
    if (typeof err.data?.message === "string") return err.data.message;
    if (typeof err.response?.data?.message === "string") {
      return err.response.data.message;
    }
  }
  return undefined;
}

interface SaveParams {
  mode: "add" | "edit";
  employee: EditEmployee;
  role: string;
  area: string;
  center: string;
  location: string;
  areaId: string;
  centerId: string;
  locationId: string;
  apiRegionCode: string;
  profilePrimaryRegion: string;
  currentPage: number;
  dispatch: AppDispatch;
  onSave: (updated: EditEmployee) => void;
  onError?: (message?: string, header?: string, variant?: string) => void;
}

async function executeSave(params: SaveParams): Promise<void> {
  const {
    mode, employee, role, area, center, location,
    areaId, centerId, locationId, apiRegionCode,
    profilePrimaryRegion, currentPage, dispatch, onSave, onError,
  } = params;
  const scope: ScopeValues = { area, center, location, areaId, centerId, locationId, regionCode: apiRegionCode };
  try {
    if (mode === "add") {
      await (dispatch(addAccess({ payload: buildAddPayload(employee, role, scope, profilePrimaryRegion), page: currentPage })) as any).unwrap();
    } else {
      await (dispatch(editAccess({ payload: buildEditPayload(employee, role, scope), page: currentPage })) as any).unwrap();
    }
    onSave({ ...employee, privRole: role, responsibilityAreaDesc: area, responsibilityCenterDesc: center, responsibilityLocationDesc: location, _addSuccess: mode === "add" ? true : undefined } as any);
  } catch (err) {
    const duplicateMsg = "Duplicate access record exists for the given NUID, privilege role, and responsibility area.";
    const alreadyAssignedMsg = "This Employee has been already assigned to this Role";
    let errorMsg = extractErrorMessage(err);
    let errorHeader = "Error";
    let errorVariant = "Error";
    if (errorMsg?.includes(duplicateMsg) || errorMsg?.includes(alreadyAssignedMsg)) {
      errorMsg = "This employee has already been assigned to this role and scope of access.";
      errorHeader = "No changes made";
      errorVariant = "Alert";
    }
    errorMsg ??= "Something went wrong. Please try again later";
    if (onError) onError(errorMsg, errorHeader, errorVariant);
  }
}

interface AreaSelectParams {
  opt: { id: string | number; label: string; value: string };
  role: string;
  apiRegionCode: string;
  dispatch: AppDispatch;
  setArea: (v: string) => void;
  setAreaId: (v: string) => void;
  setCenter: (v: string) => void;
  setCenterId: (v: string) => void;
  setLocation: (v: string) => void;
  setLocationId: (v: string) => void;
}

function applyAreaSelect(params: AreaSelectParams): void {
  const { opt, role, apiRegionCode, dispatch, setArea, setAreaId, setCenter, setCenterId, setLocation, setLocationId } = params;
  setArea(opt.value);
  const newAreaId = String(opt.id);
  setAreaId(newAreaId);
  setCenter("All"); setCenterId(""); setLocation("All"); setLocationId("");
  if (isAdministratorRole(role) && apiRegionCode && !isNullableScope(newAreaId)) {
    dispatch(fetchCenterOptions({ regionCode: apiRegionCode, areaCode: newAreaId }));
  }
}

interface CenterSelectParams {
  opt: { id: string | number; label: string; value: string };
  role: string;
  apiRegionCode: string;
  areaId: string;
  dispatch: AppDispatch;
  setCenter: (v: string) => void;
  setCenterId: (v: string) => void;
  setLocation: (v: string) => void;
  setLocationId: (v: string) => void;
}

function applyCenterSelect(params: CenterSelectParams): void {
  const { opt, role, apiRegionCode, areaId, dispatch, setCenter, setCenterId, setLocation, setLocationId } = params;
  setCenter(opt.value);
  const newCenterId = String(opt.id);
  setCenterId(newCenterId);
  setLocation("All"); setLocationId("");
  if (isAdministratorRole(role) && apiRegionCode && areaId && !isNullableScope(newCenterId)) {
    dispatch(fetchLocationOptions({ regionCode: apiRegionCode, areaCode: String(areaId), centerCode: newCenterId }));
  }
}

interface ResetEmployeeStateParams {
  employee: EditEmployee;
  isCrossRegionAdd: boolean;
  apiRegionCode: string;
  dispatch: AppDispatch;
  currentEmployeeNuidRef: React.MutableRefObject<string>;
  setInitialLoadComplete: (v: boolean) => void;
  setRole: (v: string) => void;
  setArea: (v: string) => void;
  setCenter: (v: string) => void;
  setLocation: (v: string) => void;
  setAreaId: (v: string) => void;
  setCenterId: (v: string) => void;
  setLocationId: (v: string) => void;
}

function resetEmployeeState(params: ResetEmployeeStateParams): void {
  const {
    employee, isCrossRegionAdd, apiRegionCode, dispatch,
    currentEmployeeNuidRef, setInitialLoadComplete,
    setRole, setArea, setCenter, setLocation,
    setAreaId, setCenterId, setLocationId,
  } = params;
  if (currentEmployeeNuidRef.current !== employee.nuid) {
    currentEmployeeNuidRef.current = employee.nuid;
    setInitialLoadComplete(false);
  }
  const newRole = isCrossRegionAdd ? "View-Only" : employee.privRole ?? "View-Only";
  setRole(newRole);
  setArea(employee.responsibilityAreaDesc ?? "All");
  setCenter(employee.responsibilityCenterDesc ?? "All");
  setLocation(employee.responsibilityLocationDesc ?? "All");
  setAreaId(String(employee.responsibilityArea ?? ""));
  setCenterId(String(employee.responsibilityCenter ?? ""));
  setLocationId(String(employee.responsibilityLocation ?? ""));
  dispatch(resetDependentDropdowns({ level: "region" }));
  if (normalizeRole(newRole) === "administrator" || isCrossRegionAdd) {
    preloadDropdownsForEmployee(dispatch, apiRegionCode, employee.responsibilityArea, employee.responsibilityCenter);
  } else {
    setInitialLoadComplete(true);
  }
}

function computeIsCrossRegionAdd(
  mode: "add" | "edit",
  employeeRegionCode: string,
  responsibilityRegion?: string | null,
): boolean {
  return (
    mode === "add" &&
    !!employeeRegionCode &&
    !!responsibilityRegion &&
    responsibilityRegion !== employeeRegionCode
  );
}

function computeApiRegionCode(
  isCrossRegion: boolean,
  employeeRegionCode: string,
  responsibilityRegion?: string | null,
): string {
  const resolved = responsibilityRegion || employeeRegionCode;
  return isCrossRegion ? employeeRegionCode : resolved;
}

function deriveInitialRole(isCrossRegion: boolean, privRole?: string | null): string {
  return isCrossRegion ? "View-Only" : privRole ?? "View-Only";
}

interface AllowedCodes {
  area: Set<string> | null;
  center: Set<string> | null;
  location: Set<string> | null;
}

/**
 * Computes the set of allowed area/center/location codes the logged-in user
 * may assign. Returns null (unrestricted) for all three when editDataPrivileges
 * is empty (profile not yet loaded) or the role is not Administrator.
 */
function computeAllowedCodes(
  role: string,
  editDataPrivileges: Privilege[],
  apiRegionCode: string,
  areaId: string,
  centerId: string,
): AllowedCodes {
  const isAdmin = isAdministratorRole(role) && editDataPrivileges.length > 0;
  if (!isAdmin) return { area: null, center: null, location: null };
  return {
    area: getAllowedAreaCodes(editDataPrivileges, apiRegionCode),
    center: getAllowedCenterCodes(editDataPrivileges, apiRegionCode, areaId),
    location: getAllowedLocationCodes(editDataPrivileges, apiRegionCode, areaId, centerId),
  };
}

type DropdownOption = { id: string | number; label: string; value: string };

interface AreaDropdownState {
  options: DropdownOption[];
  selected: DropdownOption;
}

/**
 * Resolves the area dropdown options and selected value, handling the
 * cross-region view-only scoped case separately from the standard case.
 */
function resolveAreaDropdownState(
  allowScopedAreaForViewOnly: boolean,
  enforceAllScope: boolean,
  resolvedScopedAreaOptions: DropdownOption[],
  resolvedSelectAreaOptions: DropdownOption[],
  areaFallback: string[],
  area: string,
  isLoading: boolean,
): AreaDropdownState {
  if (allowScopedAreaForViewOnly) {
    return {
      options: resolvedScopedAreaOptions,
      selected: resolveSelectedOption(false, resolvedSelectAreaOptions, area, isLoading),
    };
  }
  return {
    options: resolveDropdownOptions(enforceAllScope, resolvedSelectAreaOptions, areaFallback),
    selected: resolveSelectedOption(enforceAllScope, resolvedSelectAreaOptions, area, isLoading),
  };
}

/**
 * Returns true when a dropdown sync effect should be skipped.
 * Skips when the load is not yet complete, the role is LMP, or the role is
 * View-Only — because View-Only enforces "All" for every scope field and the
 * sync effect would otherwise override that with stale Administrator values
 * from the Redux store that were loaded before the role change.
 */
function shouldSkipDropdownSync(initialLoadComplete: boolean, role: string): boolean {
  return !initialLoadComplete || isLmpRole(role) || isViewOnlyRole(role);
}

/** Marks initial load complete once dropdown loading finishes. */
function markLoadCompleteIfReady(loadingDropdowns: boolean, initialLoadComplete: boolean, fetchDispatched: boolean, setter: (v: boolean) => void): void {
  // Only resolve once a fetch has actually started and then finished (loadingDropdowns true→false),
  // OR when no fetch was needed (fetchDispatched=false means View-Only/LMP path, no API call required).
  if (!initialLoadComplete && !loadingDropdowns && fetchDispatched) setter(true);
}

/** Fetches role options for the given NUID if available. */
function loadRoleOptionsIfNeeded(nuid: string | undefined, dispatch: AppDispatch): void {
  if (nuid) dispatch(fetchRoleOptions({ nuid }));
}
function selectRoleOptions(state: RootState): string[] {
  return state.manageAccess.roleOptions ?? [];
}
function selectCenterOpts(state: RootState): { id: string | number; label: string; value: string }[] {
  return state.selectUBTSlice.centerOptions ?? [];
}
function selectLocationOpts(state: RootState): { id: string | number; label: string; value: string }[] {
  return state.selectUBTSlice.locationOptions ?? [];
}
function selectLoadingDropdowns(state: RootState): boolean {
  return state.selectUBTSlice.loadingDropdowns ?? false;
}
function selectCurrentPage(state: RootState): number {
  return state.manageAccess.currentPage ?? 1;
}
function selectSaving(state: RootState): boolean {
  return state.manageAccess.saving ?? false;
}
function selectAreaOpts(state: RootState): { id: string | number; label: string; value: string }[] {
  return state.selectUBTSlice.areaOptions ?? [];
}
function selectPrimaryRegion(state: RootState): string {
  return (state as any).profile?.profile?.primaryRegion ?? "";
}
function selectEditDataPrivileges(state: RootState): Privilege[] {
  return (state as any).profile?.profile?.editDataPrivileges ?? [];
}


function EditAccessForm({
  employee,
  onClose,
  onSave,
  onError,
  mode = "edit",
}: Readonly<EditAccessFormProps>): JSX.Element {
  const dispatch = useDispatch<AppDispatch>();
  const roleOptions = useSelector(selectRoleOptions);
  const selectCenterOptions = useSelector(selectCenterOpts);
  const selectLocationOptions = useSelector(selectLocationOpts);
  const loadingDropdowns = useSelector(selectLoadingDropdowns);
  const currentPage = useSelector(selectCurrentPage);
  const roleOptionsLoading = roleOptions.length === 0 && Boolean(employee?.nuid);
  const profilePrimaryRegion = useSelector(selectPrimaryRegion);
  /** editDataPrivileges drives which area/center/location options the logged-in user may assign. */
  const editDataPrivileges: Privilege[] = useSelector(selectEditDataPrivileges);

  // Use primaryRegion from profile API instead of employeeRegionCode
  const isCrossRegionAdd = computeIsCrossRegionAdd(mode, profilePrimaryRegion, employee.responsibilityRegion);
  const apiRegionCode = computeApiRegionCode(isCrossRegionAdd, profilePrimaryRegion, employee.responsibilityRegion);
  const resolvedRegionCode = employee.responsibilityRegion || profilePrimaryRegion;

  const [role, setRole] = useState(() => {
    // If Administrator is present and user's primary region matches selected region, default to Administrator
    const adminOptionExists = (roleOptions.length > 0 ? roleOptions : defaultRoleOptions).some(
      (opt) => normalizeRole(opt) === "administrator"
    );
    const regionMatch = profilePrimaryRegion && (employee.responsibilityRegion === profilePrimaryRegion || !employee.responsibilityRegion);
    if (adminOptionExists && regionMatch) {
      return "Administrator";
    }
    return deriveInitialRole(isCrossRegionAdd, employee.privRole);
  });
  const [area, setArea] = useState(employee.responsibilityAreaDesc ?? "All");
  const [center, setCenter] = useState(employee.responsibilityCenterDesc ?? "All");
  const [location, setLocation] = useState(employee.responsibilityLocationDesc ?? "All");
  // *Id states hold the raw backend codes used for API calls and the save payload
  const [areaId, setAreaId] = useState<string>(String(employee.responsibilityArea ?? ""));
  const [centerId, setCenterId] = useState<string>(String(employee.responsibilityCenter ?? ""));
  const [locationId, setLocationId] = useState<string>(String(employee.responsibilityLocation ?? ""));
  
  /**
   * Tracks the current employee NUID to detect when a different employee is being edited.
   * Used to prevent stale dropdown data from previous employee renders.
   */
  const currentEmployeeNuidRef = React.useRef<string>(employee.nuid);
  const centerTouchedRef = React.useRef(false);
  const locationTouchedRef = React.useRef(false);

  /**
   * Tracks whether a dropdown fetch (fetchAreaOptions et al.) has been dispatched
   * for the current employee. Used by markLoadCompleteIfReady to guard against
   * the race where loadingDropdowns briefly reads false between resetDependentDropdowns
   * (synchronous) and fetchAreaOptions.pending (async) — which would otherwise
   * prematurely set initialLoadComplete=true and let sync effects run with empty options.
   */
  const fetchDispatchedRef = React.useRef<boolean>(false);

  /**
   * Tracks if initial dropdown load is complete for the current employee.
   * Prevents syncing stale dropdown values from Redux store during load transitions.
   */
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);

  // Reset local state and clear stale dropdown options whenever a different employee is opened for editing
  useEffect(() => {
    fetchDispatchedRef.current = false;
    centerTouchedRef.current = false;
    locationTouchedRef.current = false;
    resetEmployeeState({
      employee, isCrossRegionAdd, apiRegionCode, dispatch,
      currentEmployeeNuidRef, setInitialLoadComplete,
      setRole, setArea, setCenter, setLocation,
      setAreaId, setCenterId, setLocationId,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, employee.nuid, apiRegionCode, isCrossRegionAdd]);

  // Clear dropdown options on unmount so the next Edit open starts fresh
  useEffect(() => {
    return () => {
      dispatch(resetDependentDropdowns({ level: "region" }));
    };
  }, [dispatch]);

  /**
   * Track when initial dropdown loading completes.
   * Sets initialLoadComplete to true when loadingDropdowns becomes false,
   * enabling dropdown value synchronization from Redux store.
   *
   * fetchDispatchedRef is set to true as soon as loadingDropdowns goes true (a real
   * fetch started) so that the brief false→true→false cycle is distinguished from
   * the transient false window that occurs synchronously after resetDependentDropdowns
   * but before fetchAreaOptions.pending fires.
   */
  useEffect(() => {
    if (loadingDropdowns) {
      fetchDispatchedRef.current = true;
    }
    markLoadCompleteIfReady(loadingDropdowns, initialLoadComplete, fetchDispatchedRef.current, setInitialLoadComplete);
  }, [loadingDropdowns, initialLoadComplete]);

  useEffect(() => {
    loadRoleOptionsIfNeeded(employee?.nuid, dispatch);
  }, [dispatch, employee?.nuid]);
  const [areaOptions, setAreaOptions] = useState<string[]>(areaOptionsStatic);
  const [centerOptions, setCenterOptions] =
    useState<string[]>(centerOptionsStatic);
  const [locationOptions, setLocationOptions] = useState<string[]>(
    locationOptionsStatic,
  );
  // saving moved to redux slice
  const saving = useSelector(selectSaving);
  const selectAreaOptions = useSelector(selectAreaOpts);
  // when selectUBT area options load, ensure local `area` matches the employee's responsibilityAreaDesc
  useEffect(() => {
    if (shouldSkipDropdownSync(initialLoadComplete, role)) return;
    syncDropdownValue(selectAreaOptions, employee?.responsibilityAreaDesc, setArea, "Area");
  }, [selectAreaOptions, employee?.responsibilityAreaDesc, role, initialLoadComplete]);
  // When role changes, adjust available options. View Only -> only 'All'. Administrator -> load locations.
  useEffect(() => {
    applyRoleChange(role, isCrossRegionAdd, resolvedRegionCode, apiRegionCode, employee, dispatch, {
      setAreaOptions, setCenterOptions, setLocationOptions,
      setArea, setCenter, setLocation,
      setAreaId, setCenterId, setLocationId,
    });
  }, [role, employee, resolvedRegionCode, apiRegionCode, isCrossRegionAdd, dispatch]);

  // when selectCenterOptions load, sync local center state to employee's responsibilityCenterDesc
  useEffect(() => {
    if (centerTouchedRef.current || shouldSkipDropdownSync(initialLoadComplete, role)) return;
    syncDropdownValue(selectCenterOptions, employee?.responsibilityCenterDesc, setCenter, "Center");
  }, [selectCenterOptions, employee?.responsibilityCenterDesc, role, initialLoadComplete]);

  // when selectLocationOptions load, sync local location state to employee's responsibilityLocationDesc
  useEffect(() => {
    if (locationTouchedRef.current || shouldSkipDropdownSync(initialLoadComplete, role)) return;
    syncDropdownValue(selectLocationOptions, employee?.responsibilityLocationDesc, setLocation, "Location");
  }, [selectLocationOptions, employee?.responsibilityLocationDesc, role, initialLoadComplete]);

  const viewOnly = isViewOnlyRole(role);
  const enforceAllScope = viewOnly || isLmpRole(role);
  const allowScopedAreaForViewOnly = isCrossRegionAdd && viewOnly;
  const scopedAreaOptions =
    selectAreaOptions.length > 0
      ? selectAreaOptions
      : areaOptions.map((opt) => ({ id: opt, label: opt, value: opt }));

  // Compute allowed codes from the logged-in user's editDataPrivileges so that
  // only scopes the current user is permitted to assign are shown in the dropdowns.
  // Filtering is only relevant for the Administrator role; View-Only and LMP are
  // already locked to "All" via enforceAllScope / allowScopedAreaForViewOnly.
  //
  // Guard: only call the helpers when editDataPrivileges is non-empty.
  // getAllowedAreaCodes([]) returns new Set() (not null), which would incorrectly
  // filter out every option while the profile is still loading. An empty array
  // means "not yet loaded" here, so treat it as unrestricted (null = pass-through).
  const allowedCodes = computeAllowedCodes(role, editDataPrivileges, apiRegionCode, areaId, centerId);

  const resolvedScopedAreaOptions = filterByPrivilege(scopedAreaOptions, allowedCodes.area);
  const resolvedSelectAreaOptions = filterByPrivilege(selectAreaOptions, allowedCodes.area);
  const resolvedSelectCenterOptions = filterByPrivilege(selectCenterOptions, allowedCodes.center);
  const resolvedSelectLocationOptions = filterByPrivilege(selectLocationOptions, allowedCodes.location);

  // When the admin is restricted (allowedAreaCodes is a non-null Set, not a
  // wildcard null), suppress the static ["All"] fallback so that empty option
  // lists render "Select an option" instead of the "All" sentinel.
  const isRestrictedAdmin = isAdministratorRole(role) && allowedCodes.area !== null;
  const areaFallback = isRestrictedAdmin ? NO_FALLBACK_OPTIONS : areaOptions;
  const centerFallback = isRestrictedAdmin ? NO_FALLBACK_OPTIONS : centerOptions;
  const locationFallback = isRestrictedAdmin ? NO_FALLBACK_OPTIONS : locationOptions;

  const areaDropdown = resolveAreaDropdownState(
    allowScopedAreaForViewOnly,
    enforceAllScope,
    resolvedScopedAreaOptions,
    resolvedSelectAreaOptions,
    areaFallback,
    area,
    !initialLoadComplete,
  );
  const centerDropdownOptions = resolveDropdownOptions(enforceAllScope, resolvedSelectCenterOptions, centerFallback);
  const selectedCenterOption = resolveSelectedOption(enforceAllScope, resolvedSelectCenterOptions, center, !initialLoadComplete);
  const locationDropdownOptions = resolveDropdownOptions(
    enforceAllScope,
    resolvedSelectLocationOptions,
    locationFallback,
  );
  const selectedLocationOption = resolveSelectedOption(
    enforceAllScope,
    resolvedSelectLocationOptions,
    location,
    !initialLoadComplete,
  );

  const handleSave = () => executeSave({
    mode, employee, role, area, center, location,
    areaId, centerId, locationId, apiRegionCode,
    profilePrimaryRegion, currentPage, dispatch, onSave, onError,
  });

  const resolvedRoleOptions = roleOptions.length > 0 ? roleOptions : defaultRoleOptions;

  const namePart = `${employee.lastName}, ${employee.firstName} - ${employee.nuid}`;
  const locationLabel = mode === "add" ? formatLocation(employee.responsibilityRegionDesc) : "";
  const employeeDetailLabel = locationLabel ? `${namePart} - ${locationLabel}` : namePart;

  const handleAreaSelect = (opt: { id: string | number; label: string; value: string }) =>
    applyAreaSelect({ opt, role, apiRegionCode, dispatch, setArea, setAreaId, setCenter, setCenterId, setLocation, setLocationId });

  const handleCenterSelect = (opt: { id: string | number; label: string; value: string }) =>
    applyCenterSelect({ opt, role, apiRegionCode, areaId, dispatch, setCenter, setCenterId, setLocation, setLocationId });

  return (
    <form className="edit-access-form bg-white rounded-lg mt-6 flex flex-col justify-start w-235.5 max-w-360">
      {/* Show loader during role/dropdown loading, initial data load, or save operation to prevent stale data flicker */}
      {(roleOptionsLoading || !initialLoadComplete || loadingDropdowns || saving) && <Loader />}
      <div className="flex items-start gap-10 p-4! bg-[#F2F8FB] rounded-lg">
        {/* Left: Employee Details and Role */}
        <div className="flex-1 flex flex-col gap-6">
          <div
            className="ds-form w-full"
            data-ds-theme="vessel"
            data-ds-variant="input"
            data-ds-version="1"
          >
            <p className="p2 my-0! -medium">Employee Details</p>
            <div className="table-text text-base mt-5!">
              {employeeDetailLabel}
            </div>
          </div>
          <div
            className="ds-form w-full"
            data-ds-theme="vessel"
            data-ds-variant="input"
            data-ds-version="1"
          >
            <Dropdown
              label="Role"
              options={resolvedRoleOptions.map((opt) => ({ id: opt, label: opt, value: opt }))}
              selectedValue={{ id: role, label: role, value: role }}
              onSelect={(opt) => setRole(opt.value)}
              buttonClassName="w-full!"
              menuClassName="w-full! max-h-56 overflow-y-auto"
              itemClassName="py-1.5"
            />
          </div>
        </div>
        {/* Right: Scope of Access Dropdowns */}
        <div className="flex-1 flex flex-col gap-3">
          <div
            className="ds-form w-full"
            data-ds-theme="vessel"
            data-ds-variant="input"
            data-ds-version="1"
          >
            <p className="p2 my-0! -medium">Scope Of Details</p>
          </div>
          <div
            className="ds-form w-full"
            data-ds-theme="vessel"
            data-ds-variant="input"
            data-ds-version="1"
          >
            <Dropdown
              label="Area"
              options={areaDropdown.options}
              selectedValue={areaDropdown.selected}
              disabled={!initialLoadComplete || loadingDropdowns}
              onSelect={handleAreaSelect}
              buttonClassName="w-full!"
              menuClassName="w-full! max-h-56 overflow-y-auto"
              itemClassName="py-2.5"
            />
          </div>
          <div
            className="ds-form w-full"
            data-ds-theme="vessel"
            data-ds-variant="input"
            data-ds-version="1"
          >
            <Dropdown
              label="Center"
              options={centerDropdownOptions}
              selectedValue={selectedCenterOption}
              disabled={!initialLoadComplete || loadingDropdowns}
              onSelect={(opt) => {
                centerTouchedRef.current = true;
                handleCenterSelect(opt);
              }}
              buttonClassName="w-full!"
              menuClassName="w-full! max-h-56 overflow-y-auto"
              itemClassName="py-1.5"
            />
          </div>
          <div
            className="ds-form w-full"
            data-ds-theme="vessel"
            data-ds-variant="input"
            data-ds-version="1"
          >
            <Dropdown
              label="Location"
              options={locationDropdownOptions}
              selectedValue={selectedLocationOption}
              disabled={!initialLoadComplete || loadingDropdowns}
              onSelect={(opt) => {
                locationTouchedRef.current = true;
                setLocation(opt.value);
                setLocationId(String(opt.id));
              }}
              buttonClassName="w-full!"
              menuClassName="w-full! max-h-56 overflow-y-auto"
              itemClassName="py-1.5"
            />
          </div>
        </div>
      </div>
      {/* Action Buttons below the flex container */}
      <div className="flex flex-col-reverse gap-4! desktop:gap-3! desktop:flex-row mt-12 w-full pt-8!">
        <button
          type="button"
          className="button -secondary m-0! flex w-full! desktop:w-auto! self-stretch items-center justify-center box-border max-w-none! h-10"
          onClick={onClose}
        >
          <span className="flex items-center justify-center w-full">
            Cancel
          </span>
        </button>
        <button
          type="button"
          className="button -primary -light flex w-full! desktop:w-auto! h-10 m-0! self-stretch items-center justify-center box-border max-w-none!"
          onClick={handleSave}
        >
          <span className="flex items-center justify-center w-full">Save</span>
        </button>
      </div>
    </form>
  );
};

export default EditAccessForm;
